import { getApiError } from '@/utils/errorUtils';

const AI_CODE_MESSAGES: Record<string, string> = {
  AI_RATE_LIMITED: 'Too many AI requests — wait a minute and try again.',
  AI_QUOTA_EXCEEDED: 'Monthly AI limit reached. Upgrade your plan or wait until next month.',
  DIAGRAM_SERVICE_UNAVAILABLE: 'Architecture diagram service is unavailable — try again or use a simpler prompt.',
  DIAGRAM_RENDER_FAILED: 'Could not render the cloud architecture — try a shorter or simpler description.',
  DIAGRAM_SERVICE_ERROR: 'Diagram generation failed. Please try again.',
};

/**
 * User-facing message for AI endpoints (rewrite, diagrams, etc.).
 */
export function getAiError(error: unknown, fallback = 'AI request failed. Please try again.'): string {
  const err = error as any;
  const code = err?.data?.code || err?.response?.data?.code;
  if (code && AI_CODE_MESSAGES[code]) {
    return AI_CODE_MESSAGES[code];
  }

  const status = err?.status ?? err?.response?.status;
  if (status === 429) {
    const retryAfter = err?.data?.retryAfterSeconds ?? err?.response?.data?.retryAfterSeconds;
    return retryAfter
      ? `Too many AI requests — try again in ${retryAfter} seconds.`
      : AI_CODE_MESSAGES.AI_RATE_LIMITED;
  }
  if (status === 403) {
    const msg = getApiError(error, '');
    if (/ai actions limit|ai features are not available/i.test(msg)) {
      return msg;
    }
    return 'AI feature is not available on your plan.';
  }
  if (status === 503) {
    return getApiError(error, 'AI service is temporarily unavailable. Try again shortly.');
  }

  return getApiError(error, fallback);
}

export type ProfessionalDiagramResult =
  | { pngBase64: string; drawioXml: string; title: string }
  | { fallback: 'whiteboard'; content: string; message?: string };

export function isWhiteboardDiagramFallback(
  result: ProfessionalDiagramResult,
): result is { fallback: 'whiteboard'; content: string; message?: string } {
  return 'fallback' in result && result.fallback === 'whiteboard' && typeof result.content === 'string';
}
